import { ComponentRef, createComponent, EnvironmentInjector, Injectable } from '@angular/core';
import { Base64 } from 'js-base64';
import { EMPTY, fromEvent, interval, merge, Observable, of, throwError } from 'rxjs';
import { catchError, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { PowerDmsTokensApiModel } from '@app/api/api-models';
import { PowerDmsService } from '@app/api/api-services';
import { PowerDmsFilePickerComponent } from '../content/power-dms-file-picker/power-dms-file-picker.component';
import { TokenStorageService } from './token-storage.service';
import { ConfigService } from '@app/configuration/config.service';

export const POWER_DMS_AUTHENTICATED_MESSAGE_TYPE = 'DMS_FILEPICKER_AUTHENTICATED';

const POWER_DMS_STATE_KEY = 'power-dms-state';

export interface PowerDmsFileInfo {
  documentId: string;
  documentName: string;
  documentUrl: string;
  revisionId: string | null;
}

@Injectable()
export class PowerDmsFilePickerService {
  private userId: string;
  private tokensCache: Observable<PowerDmsTokensApiModel>;
  private powerDmsFilePickerComponentRef: ComponentRef<PowerDmsFilePickerComponent>;

  constructor(
    private readonly powerDmsService: PowerDmsService,
    private readonly tokenStorage: TokenStorageService,
    private readonly environmentInjector: EnvironmentInjector,
    private readonly configService: ConfigService
  ) {
    this.tokenStorage.userInfoChanged
      .asObservable()
      .pipe(filter((x) => x !== null && x.UserId !== this.userId))
      .subscribe((x) => {
        this.tokensCache = null;
        this.userId = x.UserId;
        this.getState();
      });
  }

  public open(onSelectionCallback: (links: string[]) => void): Observable<void>;
  public open(onSelectionCallback: (files: PowerDmsFileInfo[]) => void, useStructuredCallback: true): Observable<void>;
  public open(
    onSelectionCallback: ((links: string[]) => void) | ((files: PowerDmsFileInfo[]) => void),
    useStructuredCallback = false
  ): Observable<void> {
    return this.getTokens()
      .pipe(catchError(this.handleError.bind(this)))
      .pipe(switchMap(this.checkTokens.bind(this)))
      .pipe(tap(this.openFilePicker.bind(this, onSelectionCallback, useStructuredCallback)))
      .pipe(map(() => void 0));
  }

  private getTokens(): Observable<PowerDmsTokensApiModel> {
    return (
      this.tokensCache ?? (this.tokensCache = this.powerDmsService.getTokens().pipe(tap(this.cacheTokens.bind(this))))
    );
  }

  private cacheTokens(tokens: PowerDmsTokensApiModel): void {
    if (tokens === null) {
      return;
    }

    this.tokensCache = of(tokens).pipe(
      tap((x) => {
        if (this.isAccessTokenExpired(x.AccessToken)) {
          this.tokensCache = null;
        }
      })
    );
  }

  private isAccessTokenExpired(accessToken: string): boolean {
    return JSON.parse(Base64.decode(accessToken.split('.')[1])).exp < new Date().getTime() / 1000;
  }

  private handleError(): Observable<void> {
    this.tokensCache = null;

    return EMPTY;
  }

  private checkTokens(tokens: PowerDmsTokensApiModel): Observable<PowerDmsTokensApiModel> {
    if (tokens !== null) {
      return of(tokens);
    }

    const config = this.configService.getConfig();
    const scope = 'openid profile offline_access';
    const redirectUri = `${location.origin}/app/power-dms`;
    const audience = config.powerDMSAudience;
    const state = `${this.tokenStorage.userInfo.UserId}${new Date().getTime()}`;

    this.setState(state);

    const authorizeUrl = `${config.powerDMSURL}/authorize?response_type=code&scope=${scope}&client_id=${config.powerDMSClientId}&redirect_uri=${redirectUri}&audience=${audience}&state=${state}${config.powerDMSRoute}`;

    const height = 648;
    const width = 648;
    const left = (window.outerWidth - width) / 2 + window.screenLeft;
    const top = (window.outerHeight - height) / 2 + window.screenTop;

    const features = `height=${height},width=${width},left=${left},top=${top}`;

    const authWindow = window.open(authorizeUrl, 'PowerDMS Auth Modal', features);

    const authenticated = fromEvent<MessageEvent<PowerDmsFilePickerAuthenticationMessage>>(window, 'message')
      .pipe(filter((x) => x.origin === window.origin && x.data.type === POWER_DMS_AUTHENTICATED_MESSAGE_TYPE))
      .pipe(switchMap(() => this.getTokens()));

    const closed = interval(1000)
      .pipe(filter(() => authWindow.closed))
      .pipe(switchMap(() => throwError('PowerDMS Auth window has been closed.')));

    return merge(authenticated, closed).pipe(first());
  }

  private openFilePicker(
    onSelectionCallback: ((links: string[]) => void) | ((files: PowerDmsFileInfo[]) => void),
    useStructuredCallback: boolean,
    tokens: PowerDmsTokensApiModel
  ): void {
    this.powerDmsFilePickerComponentRef = createComponent(PowerDmsFilePickerComponent, {
      environmentInjector: this.environmentInjector
    });

    this.powerDmsFilePickerComponentRef.instance.tokens = tokens;
    this.powerDmsFilePickerComponentRef.instance.onSelectionCallback = onSelectionCallback;
    this.powerDmsFilePickerComponentRef.instance.useStructuredCallback = useStructuredCallback;

    this.powerDmsFilePickerComponentRef.instance.onClosedCallback = () => {
      this.powerDmsFilePickerComponentRef.destroy();

      document.body.removeChild(this.powerDmsFilePickerComponentRef.location.nativeElement);

      this.powerDmsFilePickerComponentRef = null;
    };

    document.body.appendChild(this.powerDmsFilePickerComponentRef.location.nativeElement);

    this.powerDmsFilePickerComponentRef.changeDetectorRef.detectChanges();
  }

  private setState(state: string): void {
    sessionStorage.setItem(POWER_DMS_STATE_KEY, state);
  }

  public getState(): string {
    const state = sessionStorage.getItem(POWER_DMS_STATE_KEY);

    sessionStorage.removeItem(POWER_DMS_STATE_KEY);

    return state;
  }
}

export interface PowerDmsFilePickerAuthenticationMessage {
  type: string;
}
